import React from "react"
import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import PasswordInput from "../components/auth/PasswordInput"
import SocialLoginButtons from "../components/auth/SocialLoginButtons"
import { AuthStatus, type LoginFormData } from "../types/auth"
import ThemeToggle from "../components/auth/ThemeToggle"

import { useAppDispatch, useAppSelector } from "../redux/hooks"
import { clearError, loginUser } from "../redux/slices/authSlice"
import { toast } from "react-toastify"
import Loading from "../components/auth/Loading"
import { telemetryService } from "../telemetry/TelemetryService";
import { TelemetryEvents } from "../telemetry/telemetryEvents";

const Login: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const { status, error, token } = useAppSelector((state) => state.auth)


  //useEffect to display login in response message

  useEffect(() => {
    if (error) {
      toast.error((error as unknown as { message: string }).message)
    }
  }, [error])
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
    rememberMe: false,
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    try {
      telemetryService.trackEvent(TelemetryEvents.SIGN_IN_BUTTON);
      await dispatch(
        loginUser({
          UserName: formData.email,
          Password: formData.password,
        })
      ).unwrap() 
      window.location.href = '/interview-setup'
    } catch (err) {
    }
  }

  useEffect(() => {
    if (status === "succeeded" && token) {
      navigate("/interview-setup")
    }
  }, [status, token, navigate])

  const isLoading = status === AuthStatus.LOADING;
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white transition-all">
      <div className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0">
        <ThemeToggle />
      </div>

      <div className="w-full items-stretch max-w-6xl px-4 h-auto sm:h-[600px] rounded-2xl overflow-hidden flex flex-col sm:flex-row">
        <div className="hidden  rounded-l-lg sm:flex sm:w-1/2  items-stretch justify-between">
          <img src="/login.jpg" className="w-full rounded-l-xl " alt="Illustration" />
        </div>

        <div className="w-full sm:w-1/2 p-6 rounded-2xl bg-gray-100 dark:bg-[#2a2a2a]">
          <div className="flex flex-col items-center mb-6">
            <div>
              <img src="./logo/green-logo.png" className="w-12 h-12 object-cover" alt="Logo" />
            </div>
            <h1 className="text-xl font-bold mt-2 text-gray-900 dark:text-white">Sign In</h1>
            <p className="text-gray-400 dark:text-muted text-sm font-normal">
              Welcome back! Please login to your account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-4 w-full">
            <input
              type="text"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-2.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white text-sm focus:ring-[#3FBBA3] focus:border-[#3FBBA3] dark:focus:ring-[#3FBBA3] dark:focus:border-[#3FBBA3]"
              placeholder="Enter Email ID"
              required
            />

            <PasswordInput
              id="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              required
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 bg-[#3FBBA3]"
                />
                <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Remember me
                </label>
              </div>
              <Link
                onClick={() => {
                  dispatch(clearError());
                  telemetryService.trackEvent(TelemetryEvents.FORGET_PASSWORD_BUTTON);
                }}
                to={isLoading ? "" : "/forgot"}
                className={`text-sm text-[#3FBBA3] hover:text-[#3FBBA3] ${isLoading ? "pointer-events-none text-accent-400" : ""}`}>
                Forgot Password?
              </Link>
            </div>


            <button
              type="submit"
              className="w-full bg-[#3FBBA3] hover:bg-[#3FBBA3] text-white font-medium py-2.5 px-4 rounded-md transition duration-150 ease-in-out mt-6 disabled:opacity-90 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {
                isLoading
                  ?
                  <Loading text="Signing in" />
                  :
                  "Sign in"
              }
            </button>
          </form>
          <SocialLoginButtons />

          <p className="mt-1 text-center text-xs text-gray-600 dark:text-gray-300">
            Don't have an account?{" "}
            <Link
              onClick={() => {
                dispatch(clearError());
                telemetryService.trackEvent(TelemetryEvents.SIGN_UP_BUTTON);
              }}
              to={isLoading ? "" : "/signup"}
              className={`text-[#3FBBA3] hover:text-[#3FBBA3] ${isLoading ? "pointer-events-none text-accent-400" : ""}`}

            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default Login
